import { useState, useEffect, useRef, useCallback } from 'react';
import { getAttachments, uploadAttachments, deleteAttachment } from '../utils/api';
import toast from 'react-hot-toast';

/* Attachments — thumbnails for every file, and a viewer you can move through
   (◀ / ▶ buttons, arrow keys, thumbnail strip) without closing and reopening. */

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function getFileIcon(mimeType, name) {
  const ext = name?.split('.').pop()?.toLowerCase();
  if (mimeType?.startsWith('image/')) return '🖼️';
  if (ext === 'pdf') return '📄';
  if (['doc','docx'].includes(ext)) return '📝';
  if (['xls','xlsx','csv'].includes(ext)) return '📊';
  if (['zip','rar'].includes(ext)) return '🗜️';
  if (mimeType?.startsWith('video/')) return '🎬';
  return '📎';
}

// What kind of preview a file gets
function kindOf(file) {
  const m = String(file.mime_type || '').toLowerCase();
  const ext = String(file.original_name || file.filename || '').split('.').pop().toLowerCase();
  if (m.startsWith('image/') || ['jpg','jpeg','png','gif','webp','bmp','svg','heic','heif'].includes(ext)) {
    return ['heic','heif'].includes(ext) || m.includes('heic') || m.includes('heif') ? 'other' : 'image';
  }
  if (m === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (m.startsWith('video/') || ['mp4','webm','mov','m4v'].includes(ext)) return 'video';
  return 'other';
}

const ATT_CSS = `
.att-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(170px,1fr));gap:12px}
.att-card{position:relative;background:#fff;border:1px solid var(--gray-200,#e5e7eb);border-radius:10px;overflow:hidden;cursor:pointer;transition:box-shadow .15s,border-color .15s}
.att-card:hover{border-color:var(--green-500,#2e7d55);box-shadow:0 4px 14px rgba(0,0,0,.10)}
.att-thumb{height:130px;background:#f3f5f4;display:flex;align-items:center;justify-content:center;overflow:hidden;position:relative}
.att-thumb img{width:100%;height:100%;object-fit:cover;display:block}
.att-thumb iframe{width:200%;height:200%;transform:scale(.5);transform-origin:0 0;border:0;pointer-events:none;position:absolute;top:0;left:0;background:#fff}
.att-thumb .ico{font-size:2.6rem}
.att-thumb .badge{position:absolute;left:6px;top:6px;background:rgba(13,59,46,.85);color:#fff;font-size:10px;font-weight:800;letter-spacing:.04em;padding:2px 6px;border-radius:4px;text-transform:uppercase}
.att-meta{padding:8px 10px}
.att-name{font-weight:600;font-size:.82rem;color:var(--gray-800,#1f2937);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.att-sub{font-size:.72rem;color:var(--gray-400,#9ca3af)}
.att-del{position:absolute;right:6px;top:6px;width:26px;height:26px;border-radius:50%;border:0;background:rgba(255,255,255,.92);color:#c62828;font-weight:800;cursor:pointer;box-shadow:0 1px 4px rgba(0,0,0,.2);z-index:2}
.att-del:hover{background:#c62828;color:#fff}

.att-view{position:fixed;inset:0;z-index:9999;background:rgba(10,18,15,.97);display:flex;flex-direction:column}
.att-top{display:flex;align-items:center;gap:10px;padding:10px 14px;color:#fff}
.att-top .t{flex:1;min-width:0}
.att-top .t b{display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-size:15px}
.att-top .t span{font-size:12px;color:#b8c7c0}
.att-btn{background:rgba(255,255,255,.12);color:#fff;border:1px solid rgba(255,255,255,.2);border-radius:8px;padding:7px 12px;font-size:13px;font-weight:600;cursor:pointer;text-decoration:none;white-space:nowrap}
.att-btn:hover{background:rgba(255,255,255,.24)}
.att-stage{flex:1;min-height:0;display:flex;align-items:center;justify-content:center;position:relative;padding:0 64px}
.att-stage img,.att-stage video{max-width:100%;max-height:100%;object-fit:contain;border-radius:4px;background:#111}
.att-stage iframe{width:100%;height:100%;border:0;background:#fff;border-radius:4px}
.att-none{color:#dfe8e3;text-align:center}
.att-none .ico{font-size:4rem}
.att-nav{position:absolute;top:50%;transform:translateY(-50%);width:48px;height:72px;border:0;border-radius:10px;background:rgba(255,255,255,.14);color:#fff;font-size:28px;cursor:pointer}
.att-nav:hover:not(:disabled){background:rgba(255,255,255,.3)}
.att-nav:disabled{opacity:.25;cursor:default}
.att-nav.prev{left:8px}.att-nav.next{right:8px}
.att-strip{display:flex;gap:8px;padding:10px 14px 14px;overflow-x:auto}
.att-strip button{flex:0 0 auto;width:78px;height:60px;border-radius:6px;border:2px solid transparent;background:#24302b;padding:0;overflow:hidden;cursor:pointer;display:flex;align-items:center;justify-content:center;color:#fff;font-size:1.5rem;opacity:.65}
.att-strip button.on{border-color:#7FD3A6;opacity:1}
.att-strip button:hover{opacity:1}
.att-strip img{width:100%;height:100%;object-fit:cover}
@media (max-width:640px){
  .att-grid{grid-template-columns:repeat(2,1fr)}
  .att-stage{padding:0 8px}
  .att-nav{width:38px;height:56px;font-size:22px;background:rgba(0,0,0,.45)}
  .att-top .hide-sm{display:none}
}
`;

function Thumb({ file, url }) {
  const kind = kindOf(file);
  const ext = String(file.original_name || '').split('.').pop();
  if (kind === 'image') return <div className="att-thumb"><img src={url} alt="" loading="lazy" /></div>;
  if (kind === 'pdf') return (
    <div className="att-thumb">
      <iframe src={`${url}#page=1&toolbar=0&navpanes=0&scrollbar=0&view=FitH`} title={file.original_name} loading="lazy" tabIndex={-1} />
      <span className="badge">PDF</span>
    </div>
  );
  if (kind === 'video') return <div className="att-thumb"><video src={`${url}#t=0.5`} preload="metadata" muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} /><span className="badge">Video</span></div>;
  return <div className="att-thumb"><span className="ico">{getFileIcon(file.mime_type, file.original_name)}</span>{ext && <span className="badge">{ext}</span>}</div>;
}

function Viewer({ files, index, setIndex, urlOf, onClose, onDelete }) {
  const file = files[index];
  const total = files.length;
  const go = useCallback(d => setIndex(i => Math.min(Math.max(i + d, 0), total - 1)), [setIndex, total]);
  const stripRef = useRef();

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [go, onClose]);

  // keep the current thumbnail in view in the strip
  useEffect(() => {
    stripRef.current?.children[index]?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [index]);

  // swipe left / right on phones
  const touch = useRef(null);
  const onTouchStart = e => { touch.current = e.touches[0].clientX; };
  const onTouchEnd = e => {
    if (touch.current == null) return;
    const dx = e.changedTouches[0].clientX - touch.current;
    if (Math.abs(dx) > 60) go(dx < 0 ? 1 : -1);
    touch.current = null;
  };

  if (!file) return null;
  const url = urlOf(file);
  const kind = kindOf(file);

  return (
    <div className="att-view" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="att-top">
        <div className="t">
          <b>{file.original_name}</b>
          <span>{index + 1} of {total} · {formatBytes(file.file_size)}{file.uploaded_at ? ` · ${new Date(file.uploaded_at).toLocaleDateString()}` : ''}</span>
        </div>
        <a className="att-btn" href={url} download={file.original_name}>⬇ Download</a>
        {/* PDFs: some phones (Android) can't show a PDF inside the page, so keep "New tab" visible for them */}
        <a className={'att-btn' + (kind === 'pdf' ? '' : ' hide-sm')} href={url} target="_blank" rel="noreferrer">↗ New tab</a>
        <button type="button" className="att-btn hide-sm" style={{ color: '#ffb4b4' }} onClick={() => onDelete(file)}>🗑 Delete</button>
        <button type="button" className="att-btn" onClick={onClose} title="Close (Esc)">✕ Close</button>
      </div>

      <div className="att-stage" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        {kind === 'image' && <img key={file.id} src={url} alt={file.original_name} />}
        {kind === 'pdf' && <iframe key={file.id} src={url} title={file.original_name} />}
        {kind === 'video' && <video key={file.id} src={url} controls autoPlay={false} />}
        {kind === 'other' && (
          <div className="att-none">
            <div className="ico">{getFileIcon(file.mime_type, file.original_name)}</div>
            <p style={{ margin: '10px 0 14px' }}>This file type can't be previewed here.</p>
            <a className="att-btn" href={url} download={file.original_name}>⬇ Download to open it</a>
          </div>
        )}
        <button type="button" className="att-nav prev" disabled={index === 0} onClick={() => go(-1)} title="Previous (←)">‹</button>
        <button type="button" className="att-nav next" disabled={index === total - 1} onClick={() => go(1)} title="Next (→)">›</button>
      </div>

      {total > 1 && (
        <div className="att-strip" ref={stripRef}>
          {files.map((f, i) => (
            <button type="button" key={f.id} className={i === index ? 'on' : ''} onClick={() => setIndex(i)} title={f.original_name}>
              {kindOf(f) === 'image' ? <img src={urlOf(f)} alt="" loading="lazy" /> : getFileIcon(f.mime_type, f.original_name)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Attachments({ jobId }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewIndex, setViewIndex] = useState(null);
  const fileInputRef = useRef();

  useEffect(() => {
    if (!jobId) { setLoading(false); return; }
    getAttachments(jobId).then(setFiles).finally(() => setLoading(false));
  }, [jobId]);

  const urlOf = useCallback(file => `/api/attachments/${jobId}/file/${file.filename}`, [jobId]);

  async function handleUpload(e) {
    const selected = Array.from(e.target.files);
    if (!selected.length) return;
    setUploading(true);
    try {
      const uploaded = await uploadAttachments(jobId, selected);
      setFiles(prev => [...uploaded, ...prev]);
      toast.success(`${uploaded.length} file(s) uploaded`);
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (e.target && 'value' in e.target) e.target.value = '';
    }
  }

  async function handleDelete(file) {
    if (!window.confirm(`Delete ${file.original_name}?`)) return;
    try {
      await deleteAttachment(jobId, file.id);
      setFiles(prev => {
        const next = prev.filter(f => f.id !== file.id);
        // keep the viewer open on the next file, or close it if nothing is left
        setViewIndex(v => (v == null ? v : next.length ? Math.min(v, next.length - 1) : null));
        return next;
      });
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    }
  }

  if (!jobId) {
    return (
      <div className="empty-state" style={{ padding: '40px 20px' }}>
        <p style={{ color: 'var(--gray-400)' }}>Save the record first to add attachments.</p>
      </div>
    );
  }

  return (
    <div>
      <style>{ATT_CSS}</style>
      <div
        style={{
          border: '2px dashed var(--gray-300)', borderRadius: 'var(--radius-lg)',
          padding: '22px', textAlign: 'center', marginBottom: 20,
          background: 'var(--gray-50)', cursor: 'pointer',
          transition: 'border-color 0.15s',
        }}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--green-500)'; }}
        onDragLeave={e => { e.currentTarget.style.borderColor = 'var(--gray-300)'; }}
        onDrop={e => {
          e.preventDefault();
          e.currentTarget.style.borderColor = 'var(--gray-300)';
          const dt = { target: { files: e.dataTransfer.files } };
          handleUpload(dt);
        }}
      >
        <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleUpload} />
        <div style={{ fontSize: '2rem', marginBottom: 8 }}>📎</div>
        <p style={{ fontWeight: 600, color: 'var(--gray-700)', marginBottom: 4 }}>
          {uploading ? 'Uploading…' : 'Click to upload or drag & drop'}
        </p>
        <p style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>Any file type — max 50MB per file</p>
      </div>

      {loading ? (
        <div className="loader"><div className="spinner" /></div>
      ) : files.length === 0 ? (
        <div className="empty-state" style={{ padding: '24px' }}>
          <p>No files attached yet.</p>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: 'var(--gray-400,#9ca3af)', marginBottom: 8 }}>
            {files.length} file{files.length === 1 ? '' : 's'}. Click any one to open it, then use ◀ ▶ (or the arrow keys) to go through the rest.
          </div>
          <div className="att-grid">
            {files.map((file, i) => (
              <div key={file.id} className="att-card" onClick={() => setViewIndex(i)} title={file.original_name}>
                <Thumb file={file} url={urlOf(file)} />
                <button type="button" className="att-del" title="Delete" onClick={e => { e.stopPropagation(); handleDelete(file); }}>✕</button>
                <div className="att-meta">
                  <div className="att-name">{file.original_name}</div>
                  <div className="att-sub">
                    {formatBytes(file.file_size)}{file.uploaded_at ? <> &nbsp;·&nbsp; {new Date(file.uploaded_at).toLocaleDateString()}</> : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {viewIndex != null && files[viewIndex] && (
        <Viewer files={files} index={viewIndex} setIndex={setViewIndex} urlOf={urlOf}
          onClose={() => setViewIndex(null)} onDelete={handleDelete} />
      )}
    </div>
  );
}
